/*
  CarrierReports.jsx - Index Component for Carrier Reports
*/

// NPM
import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Stack,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  Typography,
} from '@mui/material';

// COMPONENTS
import DateRangeSelector from '../../components/common/DateRangeSelector';
import CarrierDetail from '../../components/reports/carrier/CarrierDetail';
import CarrierReportsTable from '../../components/reports/carrier/CarrierReportsTable';
import AdvancedFiltersContainer from '../../components/common/AdvancedFiltersContainer';
import Loader from '../../components/common/Loader';
import Layout from '../../components/layouts/Layout';
import DataExportModal from '../../components/common/modals/DataExportModal';

// NETWORKING
import carrierReportsRequest from '../../requests/CarrierReportsRequest';

// FORMATTED MESSAGES
import CarrierReportsFormattedMessages from '../../messages/reports/CarrierReportsFormattedMessages';
import { ReportsRoutes } from '../../routes/ReportsRoutes';

const CarrierReports = ({
  accessLevel,
  accessType,
  errorHandler,
  navigate,
  updateActingProviderDetails,
  updateActingCustomerDetails,
}) => {
  const { getCarrierReportsData, exportCarrierReport } =
    carrierReportsRequest();
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({
    messages_sent: 0,
    direct_sent: 0,
    reminders_sent: 0,
    failed_messages: 0,
  });
  const [isSearching, setIsSearching] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [timeRange, setTimeRange] = useState(null);
  const [selectedData, setSelectedData] = useState(
    location?.state?.selectedData || null
  );

  const [currentlySearching, setCurrentlySearching] = useState({});
  const [open, setOpen] = useState(false);
  const [reset, setReset] = useState(false);
  const [filters, setFilters] = useState({
    carrierFilter: '',
    countryFilter: '',
  });

  const [filteredText, setFilteredText] = useState([]);
  const [selectorKeyRefresh, setSelectorKeyRefresh] = useState(false); // Add debounce timer ref
  const [exporting, setExporting] = useState(false);
  const [showEmailSentModal, setShowEmailSentModal] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');

  const handleCarrierReportSearch = useCallback(async () => {
    if (fromDate && toDate) {
      try {
        setData([]);
        setIsSearching(true);

        const searchParams = {
          ...filters,
          fromDate: fromDate,
          toDate: toDate,
          timeRange: timeRange,
          timeRangeStart: fromDate,
          timeRangeEnd: toDate,
        };
        console.log(searchParams);

        let results = await getCarrierReportsData(searchParams, errorHandler);
        let total = {
          messages_sent: 0,
          direct_sent: 0,
          reminders_sent: 0,
          failed_messages: 0,
        };

        results.forEach((el) => {
          total.messages_sent =
            total.messages_sent + parseInt(el.messages_sent);
          total.direct_sent = total.direct_sent + parseInt(el.direct_sent);
          total.reminders_sent =
            total.reminders_sent + parseInt(el.reminders_sent);
          total.failed_messages =
            total.failed_messages + parseInt(el.failed_messages);
        });

        setData(results || []);
        setTotals(total);
        setCurrentlySearching(searchParams);
      } catch (e) {
        console.error(e);
        setData([]);
      } finally {
        setIsSearching(false);
      }
    }
  }, [toDate, fromDate, reset]);

  const handleExportCustomerReport = async () => {
    setExporting(true);

    const response = await exportCarrierReport(
      currentlySearching,
      errorHandler
    );
    console.log('response', response);
    setShowEmailSentModal(true);
    setEmailRecipient(response);
    setExporting(false);
  };

  useEffect(() => {
    if (fromDate && toDate) handleCarrierReportSearch();
  }, [handleCarrierReportSearch]);

  const showCarrierDetail = (data) => {
    setSelectedData(data);
  };

  /*
    Convenience function to handle the updating of a form element
  */
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const newFilters = { ...filters, [name]: value };

    setFilters(newFilters);
    setReset(!reset);
  };

  const generateSearchText = useCallback(() => {
    // Destructure which filters the table is currently displaying fields on.
    let { carrierFilter, countryFilter } = currentlySearching || {};

    let filterText = [];
    let filterValues = ['Carrier Name:', 'Country:'];

    [carrierFilter, countryFilter].forEach((filter, i) => {
      if (filter && filter !== '')
        filterText.push(
          <span key={i}>
            {filterText && filterText.length === 0 ? '' : ', '}
            <strong>{filterValues[i]}</strong> {filter}
          </span>
        );
    });

    setFilteredText(filterText);
  }, [currentlySearching]);

  useEffect(() => {
    generateSearchText();
  }, [generateSearchText]);

  const clearFilters = async () => {
    // Create empty filters
    const emptyFilters = {
      carrierFilter: '',
      countryFilter: '',
    };

    // Update state
    setFilters(emptyFilters);
    setReset(!reset);
  };

  if (accessLevel === '3' && accessType === '3') {
    return (
      <Layout
        routes={ReportsRoutes}
        title={selectedData ? 'Carrier Details' : 'Carrier Reports'}
        backgroundColor="transparent"
        accessLevel={accessLevel}
        accessType={accessType}
        backButtonText={selectedData ? 'Back to Carrier Reports' : null}
        handleBackButton={() => setSelectedData(null)}
      >
        {selectedData ? (
          <CarrierDetail
            carrier={selectedData}
            errorHandler={errorHandler}
            fromDate={fromDate}
            toDate={toDate}
            navigate={navigate}
            updateActingProviderDetails={updateActingProviderDetails}
            updateActingCustomerDetails={updateActingCustomerDetails}
          />
        ) : (
          <Grid container sx={{ justifyContent: 'space-between', rowGap: 2 }}>
            <Grid size={{ xs: 12, lg: 9 }}>
              <DateRangeSelector
                key={selectorKeyRefresh ? 1 : 0}
                handleDateSearch={handleCarrierReportSearch}
                parentStartDate={fromDate}
                setParentStartDate={setFromDate}
                parentEndDate={toDate}
                setParentEndDate={setToDate}
              />

              <AdvancedFiltersContainer open={open} setOpen={setOpen}>
                <form id="filter-form">
                  <Stack
                    sx={{
                      flexDirection: { lg: 'row' },
                      gap: 2,
                      marginY: 2,
                      maxWidth: { lg: 674 },
                    }}
                  >
                    <Stack>
                      <Typography sx={{ whiteSpace: 'nowrap', marginTop: 1 }}>
                        By Field:
                      </Typography>
                    </Stack>

                    <Stack sx={{ gap: 1, flex: 1 }}>
                      <Stack
                        sx={{
                          flexDirection: { lg: 'row' },
                          gap: 2,
                          alignItems: { lg: 'center' },
                        }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          label="Carrier Name"
                          name="carrierFilter"
                          value={filters.carrierFilter}
                          onChange={(e) => handleInputChange(e)}
                          placeholder="Search Carrier Name"
                        />

                        <TextField
                          fullWidth
                          size="small"
                          label="Country"
                          name="countryFilter"
                          value={filters.countryFilter}
                          onChange={(e) => handleInputChange(e)}
                          placeholder="Search Country"
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                </form>
              </AdvancedFiltersContainer>

              <Stack
                sx={{
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 2,
                  maxWidth: { lg: 674 },
                }}
              >
                {filteredText && filteredText.length > 0 && (
                  <Button
                    color="secondary"
                    onClick={clearFilters}
                    sx={{
                      textTransform: 'none',
                      textDecoration: 'underline',
                      fontWeight: 400,
                    }}
                  >
                    {CarrierReportsFormattedMessages.clearFilter}
                  </Button>
                )}
              </Stack>
            </Grid>

            <Grid size={12}>
              {isSearching ? (
                <Loader />
              ) : data.length === 0 ? (
                <Typography>
                  {CarrierReportsFormattedMessages.noResults}
                </Typography>
              ) : (
                <>
                  <Stack
                    sx={{
                      flexDirection: 'row',
                      justifyContent:
                        filteredText && filteredText.length !== 0
                          ? 'space-between'
                          : 'flex-end',
                      alignItems: 'flex-end',
                      marginBottom: 1,
                    }}
                  >
                    {filteredText && filteredText.length > 0 && (
                      <Typography fontSize={12} marginBottom={1}>
                        {filteredText}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={2}>
                      <Button
                        className="float-right"
                        variant="contained"
                        color="secondary"
                        onClick={handleExportCustomerReport}
                        disabled={exporting}
                      >
                        Carrier Report
                      </Button>
                    </Stack>
                  </Stack>
                  <CarrierReportsTable
                    list={data}
                    showCarrierDetail={showCarrierDetail}
                    totals={totals}
                  />
                </>
              )}
            </Grid>
          </Grid>
        )}
        <DataExportModal
          show={showEmailSentModal}
          onHide={() => setShowEmailSentModal(false)}
          emailRecipient={emailRecipient}
        />
      </Layout>
    );
  }
};

export default CarrierReports;
